<template>
  <div class="col-12 ">
    <div class="card mb-5" style="margin-top: 4rem; overflow: visible">
      <div class="card-header p-0 position-relative mt-n4 mx-3 z-index-2" style="height: 60px;">
        <div class="bg-primary shadow-primary border-radius-lg pt-4 pb-3 h-100">
          <div class="d-flex justify-content-between align-items-center">
            <h6 class="text-white text-capitalize ps-3 pe-3 mb-0">
              {{ $t('guardians.addStudentTitle') }}
            </h6>
          </div>
        </div>
      </div>
   
      <div class="card-body px-0 pb-2  mx-2">
        <div class="row justify-content-center">
          <div class="col-md-8">
            <!-- Search Input -->
            <div class="form-group mb-4">
              <label class="form-label">{{ $t('guardians.searchStudentLabel') }}</label>
              <div class="input-group" :class="{'flex-row-reverse': $i18n.locale === 'ar'}">
                <span class="input-group-text">
                  <i class="fas fa-search"></i>
                </span>
                <input 
                  v-model="searchQuery"
                  @input="handleSearchInput"
                  type="text" 
                  class="form-control" 
                  :placeholder="$t('guardians.searchStudentPlaceholder')"
                >
              </div>
            </div>

            <!-- Relation Type Select -->
            <div class="form-group mb-4">
              <label class="form-label">{{ $t('guardians.relationType') }}</label>
              <select v-model="relationType" class="form-control">
                <option value="father">{{ $t('guardians.father') }}</option>
                <option value="mother">{{ $t('guardians.mother') }}</option>
                <option value="uncle">{{ $t('guardians.uncle') }}</option>
                <option value="aunt">{{ $t('guardians.aunt') }}</option>
                <option value="other">{{ $t('guardians.other') }}</option>
              </select>
            </div>

            <!-- Primary Guardian Toggle -->
            <div class="form-group mb-4">
              <div class="form-check">
                <input 
                  class="form-check-input" 
                  type="checkbox" 
                  id="isPrimary" 
                  v-model="isPrimary"
                  :disabled="relationType === 'father'"
                >
                <label class="form-check-label" for="isPrimary">
                  {{ $t('guardians.primaryGuardian') }}
                </label>
              </div>
              <small v-if="relationType === 'father'" class="text-muted">
                {{ $t('guardians.fatherIsPrimary') }}
              </small>
            </div>

        <div class="form-group mb-4">
  <div class="form-check">
    <input 
      class="form-check-input" 
      type="checkbox" 
      id="useDashboard" 
      v-model="useDashboard"
    >
    <label class="form-check-label" for="useDashboard">
      {{ $t('guardians.useDashboard') }} ({{ actualDashboardFee }} {{ $t('guardians.currency') }})
    </label>
  </div>
</div>

            <!-- Class Fee Information -->
            <div v-if="selectedStudents.length > 0" class="alert alert-info mb-4">
              <h6 class="alert-heading">{{ $t('guardians.classFeeInfo') }}</h6>
              <div v-for="student in selectedStudents" :key="student.id" class="mb-2">
                {{ formatStudentName(student) }}: 
                <span class="fw-bold">{{ classFees[student.id] || $t('guardians.feeLoading') }}</span>
              </div>
            
            </div>

            <!-- Search Results -->
            <div v-if="searchResults.length > 0" class="mb-4">
              <h6 class="text-muted mb-3">{{ $t('guardians.searchResults') }}</h6>
              <div class="table-responsive">
                <table class="table table-hover">
                  <thead>
                    <tr>
                      <th>{{ $t('guardians.studentName') }}</th>
                    <th>{{ $t('guardians.class') }}</th>
                      <th>{{ $t('guardians.classFee') }}</th>
                      <th>{{ $t('guardians.select') }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="student in searchResults" :key="student.id">
                      <td>{{ formatStudentName(student) }}</td>
                     <td>{{ getCurrentClass(student) }}</td>
                      <td>{{ studentFees[student.id] || $t('guardians.feeLoading') }}</td>
                      <td>
                        <button 
                          @click="selectStudent(student)"
                          class="btn btn-sm"
                          :class="isSelected(student.id) ? 'btn-success' : 'btn-outline-primary'"
                        >
                          <i class="fas" :class="isSelected(student.id) ? 'fa-check' : 'fa-plus'"></i>
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div v-else-if="searchQuery && !isSearching" class="alert alert-info mb-4">
              {{ $t('guardians.noSearchResults') }}
            </div>

            <!-- Selected Students -->
            <div v-if="selectedStudents.length > 0" class="mb-4">
              <h6 class="text-muted mb-3">{{ $t('guardians.selectedStudents') }}</h6>
              <div class="d-flex flex-wrap gap-2">
                <span 
                  v-for="student in selectedStudents" 
                  :key="student.id"
                  class="badge bg-primary d-flex align-items-center"
                >
                  {{ formatStudentName(student) }}
                  <button 
                    @click.stop="removeSelectedStudent(student.id)"
                    class="btn btn-sm p-0 ms-2 text-white"
                  >
                    <i class="fas fa-times"></i>
                  </button>
                </span>
              </div>
            </div>

            <!-- Action Buttons -->
            <div class="d-flex gap-3 mt-4">
              <button 
                type="button" 
                class="btn btn-primary flex-grow-1"
                @click="submit"
                :disabled="selectedStudents.length === 0 || isProcessing"
              >
                <span v-if="isProcessing" class="fas fa-spinner fa-spin ms-2"></span>
                <i v-else class="fas fa-plus ms-2"></i>
                {{ $t('guardians.addStudents') }}
              </button>
              <button 
                type="button" 
                class="btn btn-outline-secondary flex-grow-1"
                @click="cancel"
              >
                <i class="fas fa-times ms-2"></i>
                {{ $t('guardians.cancel') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, onBeforeUnmount, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useToast } from 'vue-toastification';
import api from '@/api'; 
import { useI18n } from "vue-i18n";

const { t } = useI18n();
const router = useRouter();
const toast = useToast();

const searchQuery = ref('');
const searchResults = ref([]);
const selectedStudents = ref([]);
const classFees = ref({});
const studentFees = ref({});
const relationType = ref('father');
const isPrimary = ref(true);
const isSearching = ref(false);
const isProcessing = ref(false);
const debounceTimer = ref(null);
const useDashboard = ref(false);
const actualDashboardFee = ref(0);
// Watch relationType for auto primary
watch(relationType, (newVal) => {
  isPrimary.value = newVal === 'father';
});
function debounce(fn, ms) {
  let timer;
  return function(...args) {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => fn.apply(this, args), ms);
  }
}

// Debounced search function
const debouncedSearchStudents = debounce(() => searchStudents(), 300);

// Watch relationType for auto primary
watch(relationType, (newVal) => {
  isPrimary.value = newVal === 'father';
});

// Watch selected students to fetch class fees (for display only)
watch(selectedStudents, (newStudents) => {
  if (newStudents.length > 0) {
    fetchClassFees(newStudents);
  } else {
    classFees.value = {};
  }
}, { deep: true });

// Search input with debounce
const handleSearchInput = () => {
  debouncedSearchStudents();
};


// Format student full name
const formatStudentName = (student) => {
  return `${student.first_name} ${student.father_name} ${student.last_name}`;
};

// Get current class for student
const getCurrentClass = (student) => {
  if (student.current_assignment && student.current_assignment.class) {
    return student.current_assignment.class.name;
  }
  
  if (student.class_groups && student.class_groups.length > 0) {
    const activeClassGroup = student.class_groups.find(group => group.status === 'active');
    if (activeClassGroup && activeClassGroup.class) {
      return activeClassGroup.class.name;
    }
  }
  
  return t('guardians.noClass');
};

// Fetch class fees for search results (display only)
const fetchSearchResultFees = async (students) => {
  for (const student of students) {
    try {
      const response = await api.get(`/students/${student.id}/class-fee`);
      studentFees.value[student.id] = `${response.data.class_fee} ${t('guardians.currency')}`;
    } catch (error) {
      console.error(`Failed to fetch class fee for student ${student.id}:`, error);
      studentFees.value[student.id] = t('guardians.feeNotAvailable');
    }
  }
};

// Fetch class fees for selected students (display only)
const fetchClassFees = async (students) => {
  for (const student of students) {
    try {
      const response = await api.get(`/students/${student.id}/class-fee`);
      classFees.value[student.id] = `${response.data.class_fee} ${t('guardians.currency')}`;
    } catch (error) {
      console.error(`Failed to fetch class fee for student ${student.id}:`, error);
      classFees.value[student.id] = t('guardians.feeNotAvailable');
    }
  }
};

// Search API
const searchStudents = async () => {
  if (searchQuery.value.length < 2) {
    searchResults.value = [];
    studentFees.value = {};
    return;
  }

  isSearching.value = true;
  try {
    const response = await api.get(`/students?search=${encodeURIComponent(searchQuery.value)}&per_page=5&with_class=true`);
    searchResults.value = response.data.data;
    fetchSearchResultFees(response.data.data);
  } catch (error) {
    toast.error(t('guardians.searchFailed'));
    console.error("Search error:", error);
  } finally {
    isSearching.value = false;
  }
};

onBeforeUnmount(() => {
  if (debounceTimer.value) clearTimeout(debounceTimer.value);
});

const selectStudent = (student) => {
  const index = selectedStudents.value.findIndex(s => s.id === student.id);
  if (index === -1) {
    selectedStudents.value.push(student);
  } else {
    selectedStudents.value.splice(index, 1);
    delete classFees.value[student.id];
  }
};

const isSelected = (studentId) => selectedStudents.value.some(s => s.id === studentId);

const removeSelectedStudent = (studentId) => {
  selectedStudents.value = selectedStudents.value.filter(s => s.id !== studentId);
  delete classFees.value[studentId];
};
const submit = async () => {
  if (selectedStudents.value.length === 0) return;

  isProcessing.value = true;
  try {
    const guardianId = router.currentRoute.value.params.id;

    // fetch guardian + settings (for wallet + fee check)
    const [guardianResponse, settingsResponse] = await Promise.all([
      api.get(`/guardians/${guardianId}`),
      api.get('/financial-settings')
    ]);

    const guardian = guardianResponse.data.data;
    const financialSettings = settingsResponse.data;

    const actualFee = Number(financialSettings.student_account_fee ?? 50);
    const totalFeeIfUsing = useDashboard.value ? actualFee * selectedStudents.value.length : 0;

    // wallet check (front-end convenience)
    if (useDashboard.value && Number(guardian.wallet_balance) < totalFeeIfUsing) {
      toast.error(
        t('guardians.insufficientBalance', {
          amount: (totalFeeIfUsing - Number(guardian.wallet_balance)).toFixed(2),
          currency: t('guardians.currency')
        })
      );
      isProcessing.value = false;
      return;
    }

    const addedStudents = [];

    // Capture the admin's "primary guardian" choice once, before the batch loop.
    // is_primary means "is this guardian the primary guardian FOR THIS STUDENT" —
    // it's scoped per student, not per guardian. Each selected student here is
    // (normally) getting their first/only guardian, so if the admin checked
    // "primary", every one of them should be marked primary independently.
    // (Previously this read the reactive isPrimary.value inside the loop and
    // flipped it to false after the first successful attach, which silently
    // left every sibling after the first one with is_primary = 0.)
    const wantPrimary = !!isPrimary.value;

    try {
      for (const student of selectedStudents.value) {
        // 1) If trying to set as primary, ensure student has no existing primary guardian
        if (wantPrimary) {
          try {
            const guardiansResp = await api.get(`/students/${student.id}/guardians`);
            const guardians = guardiansResp.data?.data || [];
            const existingPrimary = guardians.find(g => g.pivot?.is_primary);

            if (existingPrimary) {
              // Block assigning another primary; show translated toast and skip this student
              toast.error(
                t('guardians.alreadyHasPrimaryGuardian') ||
                'This student already has a primary guardian'
              );
              // do NOT attach as non-primary per new rule—just skip
              continue;
            }
          } catch (e) {
            // If the pre-check fails unexpectedly, fail gracefully but continue loop
            console.error('Failed to pre-check primary for student', student.id, e);
          }
        }

        // 2) Attach relation
        try {
          await api.post(`/guardians/${guardianId}/students`, {
            student_id: student.id,
            relation_type: relationType.value,
            is_primary: wantPrimary,
            use_dashboard: !!useDashboard.value
          });

          addedStudents.push(student.id);

          // 3) Success toasts
          if (useDashboard.value) {
            toast.success(
              t('guardians.studentAddedWithFee', {
                name: formatStudentName(student),
                fee: `${actualFee} ${t('guardians.currency')}`
              })
            );
            // match your earlier example: guardian gets activated when dashboard is used
            toast.success(t('guardians.guardianActivated'));
          } else {
            toast.success(
              t('guardians.studentAdded', {
                name: formatStudentName(student),
                fee: t('guardians.noFee')
              })
            );
          }

        } catch (error) {
          // Backend may respond 422 with specific messages
          if (error.response?.status === 422) {
            const msg = error.response?.data?.message;

            // If backend also enforces the single-primary rule, surface its message nicely
            if (msg && /primary/i.test(msg)) {
              toast.error(
                t('guardians.alreadyHasPrimaryGuardian') ||
                'This student already has a primary guardian'
              );
              continue; // skip this student, continue with others
            }

            // If already associated to this guardian
            if (msg && /already associated/i.test(msg)) {
              toast.error(
                `${t('guardians.studentAlreadyAssociated')}: ${formatStudentName(student)}`
              );
              continue;
            }

            // Generic 422 handling
            toast.error(msg || t('guardians.operationFailed'));
            continue;
          }

          // Re-throw non-validation errors to be caught by the outer catch
          throw error;
        }
      }

      // Navigate back if at least one student was added
      if (addedStudents.length > 0) {
        router.push(`/guardians/${guardianId}/students`);
      }
    } catch (loopErr) {
      // If something outside the 422 soft-fails happens in the loop, attempt rollback for those added
      if (addedStudents.length > 0) {
        await Promise.all(
          addedStudents.map(studentId =>
            api.delete(`/guardians/${guardianId}/students/${studentId}`)
          )
        );
      }
      throw loopErr;
    }
  } catch (error) {
    console.error('Operation failed:', error);
    if (error.response?.status !== 422) {
      toast.error(error.response?.data?.message || t('guardians.operationFailed'));
    }
  } finally {
    isProcessing.value = false;
  }
};

const cancel = () => {
  const guardianId = router.currentRoute.value.params.id;
  router.push(`/guardians/${guardianId}/students`);
};
onMounted(async () => {
  try {
    const response = await api.get('/financial-settings');
    actualDashboardFee.value = response.data.student_account_fee;
  } catch (error) {
    console.error("Failed to fetch financial settings:", error);
    actualDashboardFee.value = 50; // fallback value
  }
});
</script>

<style scoped>
.btn-primary {
  background-color: #23819B;
}
</style>